import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, imgUrl, money } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import { toast } from "sonner";
import ImageLightbox from "../../components/common/ImageLightbox";
import LowStockPill from "../../components/store/LowStockPill";
import ColorSwatchPicker from "../../components/store/ColorSwatchPicker";
import {
  Heart,
  ShieldCheck,
  Truck,
  RefreshCw,
  Star,
  Ruler,
  MapPin,
  Check,
  Tag,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import useRecentlyViewed from "../../hooks/useRecentlyViewed";
import CustomFitPicker from "../../components/store/CustomFitPicker";
import ProductReelsRail from "../../components/store/ProductReelsRail";

// Static label-mapper for the "Specifications" table from product.attributes
const SPEC_LABELS = {
  fabric: "Fabric",
  color: "Color",
  occasion: "Occasion",
  work_type: "Work / Embellishment",
  border_type: "Border",
  fit: "Fit",
  sleeve_type: "Sleeve",
  blouse_included: "Blouse Included",
  wash_care: "Wash Care",
  length: "Length",
  weight: "Weight",
  is_ready_to_ship: "Ready to Ship",
};

const formatVal = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") return ""; // skip nested objects / arrays
  return String(v);
};

// Build the payload extras the cart endpoint expects from the CustomFitPicker state.
const _fitExtras = (fit) => {
  if (!fit || !fit.measurement_mode || fit.measurement_mode === "standard_size") return {};
  const out = { measurement_mode: fit.measurement_mode };
  if (fit.clothing_type_id) out.clothing_type_id = fit.clothing_type_id;
  if (fit.measurement_profile_id) out.measurement_profile_id = fit.measurement_profile_id;
  if (fit.stitching_notes) out.stitching_notes = fit.stitching_notes.trim();
  if (fit.appointment_requested || fit.measurement_mode === "appointment_required") {
    out.appointment_requested = true;
  }
  return out;
};

function ProductCardMini({ p }) {
  return (
    <Link
      to={`/product/${p.slug}`}
      className="group block min-w-[180px] sm:min-w-[200px]"
      data-testid={`mini-card-${p.slug}`}
    >
      <div className="aspect-[3/4] bg-[hsl(var(--kb-surface-alt))] overflow-hidden mb-2">
        {p.images?.[0] ? (
          <img
            src={imgUrl(p.images[0])}
            alt={p.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full" />
        )}
      </div>
      <div className="text-xs text-[hsl(var(--kb-muted))] uppercase tracking-wider mb-1 truncate">
        {p.seller?.shop_name || "\u00A0"}
      </div>
      <div className="text-sm line-clamp-2 mb-1">{p.title}</div>
      <div className="text-sm font-medium text-[hsl(var(--kb-primary))]">{money(p.price)}</div>
    </Link>
  );
}

function PincodeChecker() {
  const [pin, setPin] = useState("");
  const [result, setResult] = useState(null);
  const onChange = (e) => {
    setPin(e.target.value.replace(/\D/g, "").slice(0, 6));
    if (result) setResult(null);
  };
  const check = (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(pin)) {
      setResult({ ok: false, msg: "Enter a valid 6-digit pincode" });
      return;
    }
    // Mock — deterministic ETA based on pincode digits
    const days = 3 + (parseInt(pin.slice(-2), 10) % 4);
    const codDigit = parseInt(pin[0], 10);
    const cod = codDigit % 2 === 0;
    setResult({
      ok: true,
      msg: `Delivery in ${days}-${days + 2} business days`,
      cod,
    });
  };
  return (
    <div className="border border-[hsl(var(--kb-border))] p-4 mb-8" data-testid="pincode-block">
      <div className="overline mb-3 flex items-center gap-2"><MapPin size={12} /> Delivery Options</div>
      <form onSubmit={check} className="flex gap-2">
        <input
          value={pin}
          onChange={onChange}
          placeholder="Enter pincode"
          className="flex-1 min-w-0 border border-[hsl(var(--kb-border))] px-3 py-2 text-sm focus:outline-none focus:border-[hsl(var(--kb-primary))]"
          data-testid="pincode-input"
          inputMode="numeric"
          aria-label="Delivery pincode"
          maxLength={6}
        />
        <button className="kb-btn-outline px-5 text-xs" data-testid="pincode-check-btn" disabled={pin.length !== 6}>
          Check
        </button>
      </form>
      {result && (
        <div
          className={`mt-3 text-sm flex items-start gap-2 ${result.ok ? "text-emerald-700" : "text-red-700"}`}
          data-testid="pincode-result"
          role="status"
        >
          {result.ok ? <Check size={16} className="mt-0.5" /> : null}
          <div>
            <div>{result.msg}</div>
            {result.ok && (
              <div className="text-xs text-[hsl(var(--kb-muted))]">
                {result.cod ? "Cash on Delivery available" : "Prepaid only"}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [fit, setFit] = useState({ measurement_mode: "standard_size" });
  const [reviews, setReviews] = useState([]);
  const [rev, setRev] = useState({ rating: 5, comment: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sizeChart, setSizeChart] = useState(null);
  const [showChart, setShowChart] = useState(false);
  const [similar, setSimilar] = useState([]);
  const [recentItems, setRecentItems] = useState([]);
  const [zoom, setZoom] = useState({ active: false, x: 50, y: 50 });
  // Fullscreen lightbox — opens on click/tap of the main image. Kept
  // separate from the hover-zoom pane so both features coexist without
  // interfering (hover-zoom on desktop mouseover, lightbox on click).
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const imgWrapRef = useRef(null);
  // Ref-not-state so touch-swipe deltas don't trigger a re-render mid-gesture.
  const touchStart = useRef(null);
  // Tracks whether the most recent touch sequence was a real swipe
  // (dx > 36px). If so we suppress the synthetic click that WebKit
  // fires after touchend so a swipe doesn't accidentally open the
  // lightbox as well as advance to the next image.
  const lastWasSwipe = useRef(false);
  const { user } = useAuth();
  const { addToCart, toggleWishlist, inWishlist } = useCart();
  const navigate = useNavigate();
  const [recent, pushRecent] = useRecentlyViewed();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const { data } = await api.get(`/products/${slug}`);
        if (!alive) return;
        setProduct(data);
        // Seed size + colour from the variant matrix. When a product
        // has colour-defining variants (e.g. foundation shades) we
        // default the picker to the first colour so price/stock/image
        // reflect a concrete SKU from render #1. Variants without a
        // colour NAME but with a `color_hex` (swatch-only shades)
        // still qualify — use the hex as the identity key.
        const firstColor = (() => {
          for (const v of data.variants || []) {
            const c = (v?.color || "").trim();
            if (c) return c;
            const h = (v?.color_hex || "").trim();
            if (h) return h;
          }
          return null;
        })();
        setColor(firstColor);
        // Default size = first size available for the chosen colour
        // (falls back to product.sizes[0] for legacy size-only products).
        const firstSize = (() => {
          if (firstColor) {
            const match = (data.variants || []).find(
              (v) => {
                const key = (v?.color || "").trim() || (v?.color_hex || "").trim();
                return key === firstColor && v?.size;
              },
            );
            if (match) return match.size;
          }
          return data.sizes?.[0]?.size || null;
        })();
        setSize(firstSize);
        setActiveImg(0);
        // Track in recently viewed (excluding current)
        pushRecent(data.slug);

        const [rRes, scRes, simRes] = await Promise.allSettled([
          api.get(`/reviews/product/${data.id}`),
          api.get(`/size-charts/by-product/${data.id}`),
          api.get(`/products/${data.slug}/similar?limit=8`),
        ]);
        if (!alive) return;
        if (rRes.status === "fulfilled") setReviews(rRes.value.data);
        setSizeChart(scRes.status === "fulfilled" ? scRes.value.data : null);
        if (simRes.status === "fulfilled") setSimilar(simRes.value.data?.items || []);
      } catch (e) {
        if (!alive) return;
        if (e?.response?.status === 404) setNotFound(true);
        else toast.error(e?.response?.data?.detail || "Could not load product");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug, pushRecent]);

  // Load Recently Viewed product cards (filter out current slug)
  useEffect(() => {
    const others = (recent || []).filter((s) => s !== slug).slice(0, 8);
    if (others.length === 0) { setRecentItems([]); return; }
    let alive = true;
    api.get(`/products/lookup/by-slugs?slugs=${encodeURIComponent(others.join(","))}`)
      .then(({ data }) => alive && setRecentItems(data?.items || []))
      .catch(() => alive && setRecentItems([]));
    return () => { alive = false; };
  }, [recent, slug]);

  // Close size-chart modal on ESC
  useEffect(() => {
    if (!showChart) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setShowChart(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showChart]);

  // Native shell only: mark the body so MobileBottomNav's floating cart
  // pill hides while our sticky Add-to-Cart / Buy-Now bar is on screen.
  // No effect in a normal web browser (CSS is gated on `.is-native-app`).
  useEffect(() => {
    document.body.classList.add("pdp-native-open");
    return () => document.body.classList.remove("pdp-native-open");
  }, []);

  const specs = useMemo(() => {
    if (!product) return [];
    const rows = [];
    const seen = new Set();
    const add = (key, val) => {
      if (val === undefined || val === null || val === "") return;
      if (seen.has(key)) return;
      seen.add(key);
      rows.push({ key, label: SPEC_LABELS[key] || key.replace(/_/g, " "), value: formatVal(val) });
    };
    add("fabric", product.fabric);
    add("color", product.color);
    add("occasion", product.occasion);
    Object.entries(product.attributes || {}).forEach(([k, v]) => add(k, v));
    add("is_ready_to_ship", product.is_ready_to_ship);
    return rows;
  }, [product]);

  if (loading) {
    return (
      <div className="px-6 md:px-12 lg:px-24 py-10" data-testid="product-detail-loading">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          <div className="aspect-[3/4] bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
          <div className="space-y-4">
            <div className="h-6 w-32 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
            <div className="h-12 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
            <div className="h-32 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div className="px-6 py-24 text-center" data-testid="product-not-found">
        <div className="overline mb-3">Not Found</div>
        <h1 className="font-display text-4xl mb-4">This piece isn't here anymore</h1>
        <Link to="/shop" className="kb-btn-primary inline-block">Browse the bazaar</Link>
      </div>
    );
  }

  // Derive colour + size options from the variant matrix. A product
  // qualifies as "colour-defining" when at least one variant carries a
  // non-empty `color` name OR a `color_hex` swatch. The identity key
  // is the colour name; if that's blank we fall back to `color_hex`
  // so unnamed swatch-only shades still uniquely address a variant.
  const productVariants = product.variants || [];
  const _variantColorKey = (v) => {
    const n = (v?.color || "").trim();
    if (n) return n;
    const h = (v?.color_hex || "").trim();
    return h || "";
  };
  const uniqueColors = (() => {
    const seen = new Map();
    for (const v of productVariants) {
      const c = _variantColorKey(v);
      if (!c || seen.has(c)) continue;
      seen.set(c, true);
    }
    return Array.from(seen.keys());
  })();
  const colorOptions = uniqueColors.map((name) => {
    // Prefer the admin-pinned hex from the first variant carrying
    // this colour so brand-specific shade names ("Warm Honey Beige")
    // render with the exact swatch the admin chose.
    let hex = null;
    let totalStock = 0;
    for (const v of productVariants) {
      if (_variantColorKey(v) !== name) continue;
      if (!hex && v?.color_hex) hex = v.color_hex;
      totalStock += Number(v?.stock || 0);
    }
    return { name, hex, disabled: totalStock <= 0 };
  });

  // When a product has a variants matrix, the currently-selected
  // (colour, size) pair dictates price/MRP/stock. Falls back to
  // size-only match when the product has no colour axis.
  const currentVariant = (() => {
    if (!productVariants.length) return null;
    const wantColor = (color || "").trim();
    const wantSize = size || "";
    return (
      productVariants.find((v) => {
        const vKey = _variantColorKey(v);
        const vSize = v?.size || "";
        if (uniqueColors.length && wantColor && vKey !== wantColor) return false;
        if (wantSize && (vSize || "") !== wantSize) return false;
        return true;
      }) || null
    );
  })();

  // Sizes valid for the currently-picked colour. Falls back to
  // `product.sizes` (already denormalised by the backend) when the
  // product has no colour axis.
  const sizeOptions = (() => {
    if (!uniqueColors.length || !color) {
      return product.sizes || [];
    }
    const matching = productVariants.filter(
      (v) => _variantColorKey(v) === color,
    );
    const out = [];
    const seen = new Set();
    for (const v of matching) {
      const s = (v?.size || "").trim();
      if (!s || seen.has(s)) continue;
      seen.add(s);
      out.push({ size: s, stock: Number(v?.stock || 0) });
    }
    return out;
  })();
  // Price preference: selected variant > lowest variant (listing) >
  // master price (no variants).
  const displayPrice = currentVariant?.price ?? product.lowest_price ?? product.price;
  const displayMrp = currentVariant?.mrp ?? product.lowest_mrp ?? product.mrp;
  const off = displayMrp > displayPrice
    ? Math.round(((displayMrp - displayPrice) / displayMrp) * 100)
    : 0;

  const addCart = async () => {
    if (!user) return toast.info("Sign in to add to cart");
    try {
      await addToCart(product.id, size, 1, {
        ..._fitExtras(fit),
        ...(color ? { color } : {}),
      });
      toast.success("Added to cart");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const buyNow = async () => {
    if (!user) return toast.info("Sign in to continue");
    try {
      await addToCart(product.id, size, 1, {
        ..._fitExtras(fit),
        ...(color ? { color } : {}),
      });
      navigate("/checkout");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not proceed to checkout");
    }
  };

  const like = async () => {
    if (!user) return toast.info("Sign in to save to wishlist");
    const added = await toggleWishlist(product.id);
    toast.success(added ? "Saved" : "Removed");
  };

  const onMouseMove = (e) => {
    const rect = imgWrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoom({ active: true, x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) return toast.info("Sign in to review");
    if (!rev.comment.trim()) return toast.error("Please add a comment");
    setSubmitting(true);
    try {
      await api.post("/reviews", { product_id: product.id, rating: rev.rating, comment: rev.comment.trim() });
      const r = await api.get(`/reviews/product/${product.id}`);
      setReviews(r.data);
      setRev({ rating: 5, comment: "" });
      toast.success("Review submitted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  // Prefer the active variant's own image so the customer sees the
  // exact shade they've picked. Falls back to product.images.
  const galleryImages = (() => {
    const vi = currentVariant?.images || [];
    if (vi.length) return vi;
    return product.images || [];
  })();
  const mainSrc = galleryImages[activeImg]
    ? imgUrl(galleryImages[activeImg])
    : (galleryImages[0] ? imgUrl(galleryImages[0]) : null);
  const imgCount = galleryImages.length || 0;
  const prevImage = () => setActiveImg((i) => (i - 1 + imgCount) % imgCount);
  const nextImage = () => setActiveImg((i) => (i + 1) % imgCount);

  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; lastWasSwipe.current = false; };
  const onTouchEnd = (e) => {
    if (touchStart.current == null || imgCount < 2) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(dx) > 36) {
      lastWasSwipe.current = true;  // suppress subsequent synthetic click
      dx < 0 ? nextImage() : prevImage();
    }
    touchStart.current = null;
  };
  // Open the fullscreen lightbox — but only if the touch wasn't a
  // swipe (which the browser reports to us as an equivalent click).
  const openLightbox = () => {
    if (lastWasSwipe.current) { lastWasSwipe.current = false; return; }
    if (mainSrc) setLightboxOpen(true);
  };

  return (
    <div className="px-6 md:px-12 lg:px-24 py-10 pdp-native-scroll-pad" data-testid="product-detail-page">
      <div className="text-xs text-[hsl(var(--kb-muted))] mb-6 uppercase tracking-wider">
        <Link to="/shop">Shop</Link> / <Link to={`/shop/${product.category?.slug}`}>{product.category?.name}</Link> / {product.title}
      </div>

      {/* grid-cols-1 = minmax(0, 1fr): one wide block can't widen the image column past a small phone. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
        <div>
          <div
            ref={imgWrapRef}
            className="aspect-[3/4] bg-[hsl(var(--kb-surface-alt))] overflow-hidden mb-3 relative cursor-zoom-in select-none"
            onMouseEnter={() => mainSrc && setZoom((z) => ({ ...z, active: true }))}
            onMouseLeave={() => setZoom((z) => ({ ...z, active: false }))}
            onMouseMove={mainSrc ? onMouseMove : undefined}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            onClick={openLightbox}
            data-testid="pdp-main-image"
          >
            {mainSrc ? (
              <>
                {/* object-contain (Feb 2026) instead of object-cover so
                    the whole uploaded image is visible — no more
                    text-lost-on-left issues when the source aspect
                    doesn't match the 3:4 tile. */}
                <img src={mainSrc} alt={product.title} className="w-full h-full object-contain" />
                {zoom.active && (
                  <div
                    className="hidden md:block absolute inset-0 pointer-events-none"
                    style={{
                      backgroundImage: `url(${mainSrc})`,
                      backgroundRepeat: "no-repeat",
                      backgroundSize: "200%",
                      backgroundPosition: `${zoom.x}% ${zoom.y}%`,
                    }}
                    data-testid="pdp-zoom-pane"
                  />
                )}
                {imgCount > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); prevImage(); }}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 shadow z-10"
                      aria-label="Previous image"
                      data-testid="pdp-prev-image"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); nextImage(); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 shadow z-10"
                      aria-label="Next image"
                      data-testid="pdp-next-image"
                    >
                      <ChevronRight size={18} />
                    </button>
                    <div
                      className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 text-white text-[10px] tracking-wider px-2 py-0.5 z-10"
                      data-testid="pdp-image-counter"
                    >
                      {activeImg + 1} / {imgCount}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[hsl(var(--kb-muted))] text-xs uppercase tracking-wider" data-testid="pdp-no-image">No image</div>
            )}
          </div>
          <div className="grid grid-cols-5 gap-2">
            {galleryImages.map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                className={`aspect-square overflow-hidden border ${i === activeImg ? "border-[hsl(var(--kb-primary))]" : "border-transparent"}`}
                data-testid={`thumb-${i}`}
              >
                <img src={imgUrl(img)} alt="" className="w-full h-full object-contain" />
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="overline mb-2">{product.seller?.shop_name}</div>
          <h1 className="font-display text-4xl lg:text-5xl leading-tight mb-4">{product.title}</h1>
          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} size={14} className={i < Math.round(product.rating_avg) ? "fill-[hsl(var(--kb-accent))] text-[hsl(var(--kb-accent))]" : "text-[hsl(var(--kb-border))]"} />
              ))}
            </div>
            <span className="text-xs text-[hsl(var(--kb-muted))]">{product.review_count} reviews</span>
          </div>

          <div className="flex items-baseline gap-4 mb-6 pb-6 border-b border-[hsl(var(--kb-border))]">
            <span className="text-3xl font-display text-[hsl(var(--kb-primary))]" data-testid="pdp-price">{money(displayPrice)}</span>
            {displayMrp > displayPrice && (
              <>
                <span className="text-lg text-[hsl(var(--kb-muted))] line-through" data-testid="pdp-mrp">{money(displayMrp)}</span>
                <span className="text-sm text-[hsl(var(--kb-primary))]">{off}% off</span>
              </>
            )}
            <LowStockPill
              stock={currentVariant?.stock ?? product.stock_total}
              className="ml-1"
            />
          </div>

          <p className="text-[hsl(var(--kb-muted))] leading-relaxed mb-6">{product.description}</p>

          <div className="grid grid-cols-2 gap-3 mb-6 text-sm">
            {/* Only show the master `product.color` label when the
                product has no colour-defining variants — otherwise
                the ColorSwatchPicker below owns the "current colour"
                display and this line becomes a stale duplicate. */}
            {product.color && !uniqueColors.length && (
              <div>
                <span className="text-[hsl(var(--kb-muted))]">Color:</span>{" "}
                <b>{product.color}</b>
              </div>
            )}
            {product.brand && <div><span className="text-[hsl(var(--kb-muted))]">Brand:</span> <b>{product.brand}</b></div>}
          </div>

          {colorOptions.length > 0 && (
            <ColorSwatchPicker
              colors={colorOptions}
              value={color}
              onChange={(next) => {
                setColor(next);
                // Re-anchor size to something valid for the new colour.
                const validSizes = productVariants
                  .filter((v) => _variantColorKey(v) === next)
                  .map((v) => (v?.size || "").trim())
                  .filter(Boolean);
                if (validSizes.length && !validSizes.includes(size || "")) {
                  setSize(validSizes[0]);
                }
                // Reset gallery cursor so the swap is instant and the
                // customer isn't left staring at index 5 of the old
                // colour's images.
                setActiveImg(0);
              }}
              label={product.category?.slug?.includes("beauty") ? "Select Shade" : "Select Colour"}
            />
          )}

          {sizeOptions.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="overline">Select Size</div>
                {sizeChart && (
                  <button
                    onClick={() => setShowChart(true)}
                    className="text-xs uppercase tracking-wider underline text-[hsl(var(--kb-primary))] flex items-center gap-1"
                    data-testid="size-guide-btn"
                  >
                    <Ruler size={12} /> Size Guide
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {sizeOptions.map((s) => (
                  <button
                    key={s.size}
                    onClick={() => setSize(s.size)}
                    data-testid={`size-${s.size}`}
                    className={`border px-5 py-3 text-sm ${size === s.size ? "border-[hsl(var(--kb-primary))] bg-[hsl(var(--kb-primary))] text-white" : "border-[hsl(var(--kb-border))] hover:border-[hsl(var(--kb-primary))]"}`}
                  >
                    {s.size}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.supports_custom_measurement && (
            <div className="mb-6">
              <CustomFitPicker product={product} value={fit} onChange={setFit} />
            </div>
          )}

          <div className="flex flex-wrap gap-3 mb-6">
            <button className="kb-btn-primary flex-1 min-w-[140px]" onClick={addCart} data-testid="add-to-cart-btn">
              Add to Cart
            </button>
            <button className="kb-btn-outline flex-1 min-w-[140px]" onClick={buyNow} data-testid="buy-now-btn">
              Buy Now
            </button>
            <button
              onClick={like}
              className="border border-[hsl(var(--kb-border))] px-5 py-3 flex items-center justify-center hover:border-[hsl(var(--kb-primary))] shrink-0"
              aria-label="Wishlist"
              data-testid="detail-wishlist-btn"
            >
              <Heart size={18} className={inWishlist?.(product.id) ? "fill-[hsl(var(--kb-primary))] text-[hsl(var(--kb-primary))]" : ""} />
            </button>
          </div>

          <PincodeChecker />

          <div className="grid grid-cols-3 gap-4 py-6 border-t border-[hsl(var(--kb-border))] text-xs text-[hsl(var(--kb-muted))]">
            <div className="flex items-center gap-2"><Truck size={14} /> Free shipping above ₹999</div>
            <div className="flex items-center gap-2"><RefreshCw size={14} /> 7-day returns</div>
            <div className="flex items-center gap-2"><ShieldCheck size={14} /> 100% genuine products</div>
          </div>
        </div>
      </div>

      {/* Specifications */}
      {specs.length > 0 && (
        <div className="mt-20 max-w-3xl" data-testid="specs-block">
          <div className="overline mb-3 flex items-center gap-2"><Tag size={12} /> Specifications</div>
          <h2 className="font-display text-3xl mb-6">Product Details</h2>
          <div className="border border-[hsl(var(--kb-border))]">
            <table className="w-full text-sm">
              <tbody>
                {specs.map((s, i) => (
                  <tr key={s.key} className={i % 2 === 0 ? "bg-[hsl(var(--kb-surface-alt))]/40" : ""}>
                    <td className="p-3 w-1/3 text-[hsl(var(--kb-muted))] capitalize">{s.label}</td>
                    <td className="p-3 font-medium">{s.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Reviews */}
      <div className="mt-20 max-w-3xl">
        <div className="overline mb-3">Reviews</div>
        <h2 className="font-display text-3xl mb-6">What our patrons say</h2>
        {user && (
          <form onSubmit={submitReview} className="mb-8 border border-[hsl(var(--kb-border))] p-5">
            <div className="flex gap-1 mb-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <button
                  type="button"
                  key={i}
                  onClick={() => setRev((r) => ({ ...r, rating: i + 1 }))}
                  data-testid={`review-star-${i + 1}`}
                >
                  <Star size={22} className={i < rev.rating ? "fill-[hsl(var(--kb-accent))] text-[hsl(var(--kb-accent))]" : "text-[hsl(var(--kb-border))]"} />
                </button>
              ))}
            </div>
            <textarea
              value={rev.comment}
              onChange={(e) => setRev({ ...rev, comment: e.target.value })}
              className="w-full border border-[hsl(var(--kb-border))] p-3 text-sm"
              rows={3}
              placeholder="Share your experience…"
              data-testid="review-comment"
            />
            <button className="kb-btn-primary mt-3" disabled={submitting} data-testid="review-submit">
              {submitting ? "Submitting…" : "Submit Review"}
            </button>
          </form>
        )}
        {reviews.length === 0 && <div className="text-[hsl(var(--kb-muted))]">Be the first to review.</div>}
        <div className="space-y-6">
          {reviews.map((r) => (
            <div key={r.id} className="pb-6 border-b border-[hsl(var(--kb-border))]">
              <div className="flex items-center gap-2 mb-1">
                <div className="font-medium">{r.user_name}</div>
                <div className="flex">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} size={12} className={i < r.rating ? "fill-[hsl(var(--kb-accent))] text-[hsl(var(--kb-accent))]" : "text-[hsl(var(--kb-border))]"} />
                  ))}
                </div>
              </div>
              <p className="text-sm text-[hsl(var(--kb-muted))]">{r.comment}</p>
            </div>
          ))}
        </div>
      </div>

      {/* You May Also Like */}
      {similar.length > 0 && (
        <div className="mt-20" data-testid="similar-block">
          <div className="overline mb-2">You may also like</div>
          <h2 className="font-display text-3xl mb-6">Similar pieces</h2>
          <div className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2 snap-x">
            {similar.map((p) => (
              <div key={p.id} className="snap-start"><ProductCardMini p={p} /></div>
            ))}
          </div>
        </div>
      )}

      {/* Seen in Reels — Apple 5.2.3 kill-switch respected via the
          rail's own guard (checks this tenant's branding.reels_enabled
          internally). */}
      <ProductReelsRail productId={product?.id} />

      {/* Recently Viewed */}
      {recentItems.length > 0 && (
        <div className="mt-16" data-testid="recently-viewed-block">
          <div className="overline mb-2">Recently viewed</div>
          <h2 className="font-display text-3xl mb-6">Continue browsing</h2>
          <div className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2 snap-x">
            {recentItems.map((p) => (
              <div key={p.id} className="snap-start"><ProductCardMini p={p} /></div>
            ))}
          </div>
        </div>
      )}

      {/* Sticky bottom action bar — native app only (App Store Guideline 4.2).
          Sits above MobileBottomNav (56px) + safe-area inset so a customer
          always has Add-to-Cart / Buy-Now within thumb reach, matching
          standard iOS/Android e-commerce app UX. */}
      <div
        className="native-only fixed left-0 right-0 z-30 bg-white/95 backdrop-blur border-t border-[hsl(var(--kb-border))] gap-2 px-3 py-2 items-stretch"
        style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 3.5rem)" }}
        data-testid="pdp-native-action-bar"
      >
        <button
          onClick={like}
          className="border border-[hsl(var(--kb-border))] rounded-xl px-4 flex items-center justify-center shrink-0"
          aria-label="Wishlist"
          data-testid="pdp-native-wishlist-btn"
        >
          <Heart
            size={20}
            className={inWishlist?.(product.id) ? "fill-[hsl(var(--kb-primary))] text-[hsl(var(--kb-primary))]" : ""}
          />
        </button>
        <button
          className="flex-1 rounded-xl bg-white border-2 border-[hsl(var(--kb-primary))] text-[hsl(var(--kb-primary))] font-medium uppercase tracking-wider text-sm"
          onClick={addCart}
          data-testid="pdp-native-add-to-cart-btn"
        >
          Add to Cart
        </button>
        <button
          className="flex-1 rounded-xl bg-[hsl(var(--kb-primary))] text-white font-medium uppercase tracking-wider text-sm"
          onClick={buyNow}
          data-testid="pdp-native-buy-now-btn"
        >
          Buy Now
        </button>
      </div>

      {showChart && sizeChart && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center p-0 md:p-4"
          onClick={() => setShowChart(false)}
          data-testid="size-chart-modal"
        >
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-[hsl(var(--kb-border))] flex justify-between items-start sticky top-0 bg-white">
              <div>
                <div className="overline">Size Guide</div>
                <h2 className="font-display text-2xl md:text-3xl">{sizeChart.name}</h2>
                <div className="text-xs text-[hsl(var(--kb-muted))] mt-1">All measurements in {sizeChart.unit}</div>
              </div>
              <button onClick={() => setShowChart(false)} className="text-2xl leading-none text-[hsl(var(--kb-muted))]" aria-label="Close">×</button>
            </div>
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-[hsl(var(--kb-surface-alt))]">
                      <th className="p-3 border border-[hsl(var(--kb-border))] text-left">Size</th>
                      {(sizeChart.columns || []).map((c, i) => (
                        <th key={i} className="p-3 border border-[hsl(var(--kb-border))] text-left">{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(sizeChart.rows || []).map((r, i) => (
                      <tr key={i}>
                        <td className="p-3 border border-[hsl(var(--kb-border))] font-medium">{r.size}</td>
                        {(r.values || []).map((v, j) => (
                          <td key={j} className="p-3 border border-[hsl(var(--kb-border))]">{v}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {sizeChart.notes && <p className="mt-4 text-xs text-[hsl(var(--kb-muted))] leading-relaxed">{sizeChart.notes}</p>}
            </div>
          </div>
        </div>
      )}

      <ImageLightbox
        open={lightboxOpen}
        images={galleryImages}
        initialIndex={activeImg}
        onClose={() => setLightboxOpen(false)}
        alt={product.title}
      />
    </div>
  );
}
